'use client';

import Link from 'next/link';
import { useEffect, useState } from 'react';
import { getRecentlyViewed, type StoredProduct } from '@/lib/wishlist';

// Renders a horizontal rail of the visitor's recently-viewed products.
// Returns null until hydration so SSR doesn't flash a hardcoded list.
// `excludeId` lets the product detail page hide the current product
// from its own recently-viewed rail.

export function RecentlyViewed({
  title = 'Recently Viewed',
  excludeId,
  max = 6,
}: {
  title?:    string;
  excludeId?: string;
  max?:       number;
}) {
  const [items, setItems] = useState<StoredProduct[] | null>(null);

  useEffect(() => {
    const all = getRecentlyViewed();
    const filtered = excludeId ? all.filter((p) => p.id !== excludeId) : all;
    setItems(filtered.slice(0, max));
  }, [excludeId, max]);

  if (!items || items.length === 0) return null;

  return (
    <section className="border-t border-ink/10 px-6 lg:px-12 py-12 md:py-16 bg-bone">
      <div className="max-w-[1400px] mx-auto">
        <h2 className="text-xs sm:text-xs uppercase tracking-[0.14em] text-ink/70 font-medium mb-8">
          {title}
        </h2>
        <div className="flex gap-4 md:gap-6 overflow-x-auto -mx-6 lg:-mx-12 px-6 lg:px-12 pb-2 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden">
          {items.map((p) => (
            <Link
              key={p.id}
              href={`/shop/${p.id}`}
              className="group shrink-0 w-[160px] md:w-[200px]"
            >
              <div className="aspect-square bg-white mb-3 overflow-hidden">
                {p.image && (
                  // eslint-disable-next-line @next/next/no-img-element
                  <img
                    src={p.image}
                    alt={p.name}
                    className="w-full h-full object-contain p-[6%] group-hover:scale-[1.03] transition-transform duration-700"
                  />
                )}
              </div>
              <h3 className="font-display tracking-[0.06em] font-semibold text-xs text-ink mb-1 leading-tight line-clamp-2">
                {p.name}
              </h3>
              <p className="text-xs text-ink/60 font-numerals">{p.price}</p>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}
